import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const SubscriptionChangeModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  currentPlan, 
  newPlan, 
  isUpgrade,
  loading,
  currentPlanPrice,
  newPlanPrice
}) => {
  if (!isOpen) return null;

  const formatPlanName = (tierKey) => {
    const names = {
      'tier1': 'Basic',
      'tier2': 'Professional', 
      'tier3': 'Business',
      'free': 'Free'
    };
    return names[tierKey] || tierKey;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '$0';
    if (typeof price === 'number') {
      return `$${price}`;
    }
    return price.startsWith('$') ? price : `$${price}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-background0 opacity-75" onClick={onClose}></div>
        </div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="bg-card rounded-md text-gray-400 hover:text-muted-foreground focus:outline-none"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                isUpgrade ? 'bg-blue-100' : 'bg-yellow-100'
              }`}>
                {isUpgrade ? (
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                )}
              </div>

              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-foreground">
                  Confirm {isUpgrade ? 'Upgrade' : 'Downgrade'}
                </h3>
                
                <div className="mt-4 space-y-3">
                  <div className="bg-background rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Current Plan:</span>
                      <span className="font-medium">{formatPlanName(currentPlan)} ({formatPrice(currentPlanPrice)}/mo)</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-muted-foreground">New Plan:</span>
                      <span className="font-medium">{formatPlanName(newPlan)} ({formatPrice(newPlanPrice)}/mo)</span>
                    </div>
                  </div>

                  {isUpgrade ? (
                    <div className="bg-accent border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">What happens next:</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Your plan upgrades immediately</li>
                        <li>• You'll be charged {formatPrice(newPlanPrice)}/month starting now</li>
                        <li>• You'll receive a prorated credit for unused time on your current plan</li>
                        <li>• New features are available right away</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-medium text-yellow-900 mb-2">What happens next:</h4>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        <li>• You keep your current plan until the end of this billing period</li>
                        <li>• Your plan will change to {formatPlanName(newPlan)} at the next renewal</li>
                        <li>• You'll be charged {formatPrice(newPlanPrice)}/month starting next billing cycle</li>
                        <li>• No refunds for the current billing period</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed'
                  : isUpgrade 
                    ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    : 'bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500'
              }`}
            >
              {loading ? 'Processing...' : 'Proceed with Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-card-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionChangeModal;